import type { LegendContentResource } from '@/features/legends/api/resources/legend'
import { createLegacyIdFixer } from '@/features/legends/editor/legacy-ids'
import type { LegendDocument } from '@/features/legends/editor/legend-document'
import {
  mapInfoLineResourcesToInfoLines,
  mapInfoLinesToResources,
  mapLegendContentResourceToOptions,
  mapLegendOptionsToContentResource,
} from '@/features/legends/options/legend-options-mappers'
import {
  mapQuestionResourceToQuestion,
  mapQuestionToResource,
} from '@/features/legends/questions/question-mappers'
import {
  mapRoutingResourceToRouting,
  mapRoutingToResource,
} from '@/features/legends/routing/routing-mappers'
import {
  mapTemplateResourceToTemplate,
  mapTemplateToResource,
} from '@/features/legends/templates/template-mappers'

/**
 * Maps a legend version's content to the document the builder edits. The API
 * keeps the questions in the first story; the builder has a single list.
 * Question IDs from before engine v1.14 are fixed, and so are the references
 * to them.
 *
 * @param resource
 */
export function mapLegendContentResourceToDocument(
  resource: LegendContentResource,
): LegendDocument {
  const ids = createLegacyIdFixer()
  return {
    options: mapLegendContentResourceToOptions(resource, ids),
    questions: (resource.stories?.[0]?.questions ?? []).map((question) =>
      mapQuestionResourceToQuestion(question, ids),
    ),
    infoLines:
      resource.infoLines &&
      mapInfoLineResourcesToInfoLines(resource.infoLines, ids),
    routing: resource.routing
      ? mapRoutingResourceToRouting(resource.routing, ids)
      : undefined,
    templates: resource.templates?.map((template) =>
      mapTemplateResourceToTemplate(template, ids),
    ),
  }
}

/**
 * Maps the builder's document to a legend version's content.
 *
 * @param document
 */
export function mapLegendDocumentToContentResource(
  document: LegendDocument,
): LegendContentResource {
  return {
    ...mapLegendOptionsToContentResource(document.options),
    templates: document.templates?.map(mapTemplateToResource),
    routing: document.routing && mapRoutingToResource(document.routing),
    infoLines:
      document.infoLines && mapInfoLinesToResources(document.infoLines),
    stories: [
      {
        id: 'ROOT',
        first: true,
        questions: document.questions.map(mapQuestionToResource),
      },
    ],
  }
}
